import { useState, KeyboardEvent, SetStateAction, Dispatch } from "react";
import { SalesReportSummary } from "../../../types/sale-reports.type";
import { formatNumber, numberWithCommas } from "../../../utils";
import { updateSaleTarget } from "../../../services";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/Edit";

const HEAD_OFFICE_TAG_TEXT = "ตั้งโดย HO";
const NOT_SET_TEXT = "ยังไม่ตั้งเป้า";

interface SaleTargetCardProps {
  reportSummary: SalesReportSummary;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function SaleTargetCard({
  reportSummary,
  setTriggerFetch,
}: SaleTargetCardProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const { reportId } = useParams<{ reportId: string }>();
  const [openInputSaleTarget, setOpenInputSaleTarget] =
    useState<boolean>(false);
  const [saleTarget, setSaleTarget] = useState<number | null>();

  const isHeadOfficeTarget = reportSummary.sales_target_source === "HEAD_OFFICE";
  const isTargetNotSet = reportSummary.sales_target_source === "NOT_SET";

  const onUpdateSaleTarget = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (reportId && saleTarget && e.key === "Enter") {
      e.preventDefault();
      await updateSaleTarget(reportId, saleTarget);
      setTriggerFetch((prev) => prev + 1);
      setOpenInputSaleTarget(false);
    }
  };

  return (
    <Card
      sx={{
        bgcolor: (theme) => theme.palette.primary.main,
        height: "100%",
        display: "flex",
        alignItems: "flex-end",
        boxShadow: "none",
        borderRadius: "12px",
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          minWidth: 0,
          color: "#fff",
          padding: 0,
          "&:last-child": { padding: 0 },
        }}
      >
        <Typography variant="subtitle1">เป้ายอดขาย</Typography>
        {isTargetNotSet ? (
          <Typography variant="h6" fontWeight={600} sx={{ color: "#FFE941" }}>
            {NOT_SET_TEXT}
          </Typography>
        ) : isHeadOfficeTarget ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h5" fontWeight={600} sx={{ color: "#FFE941" }}>
              {formatNumber(reportSummary.sales_target)}
            </Typography>
            <Chip size="small" label={HEAD_OFFICE_TAG_TEXT} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.25)" }} />
          </Stack>
        ) : !openInputSaleTarget &&
        reportSummary.sales_target &&
        reportSummary.sales_target !== 0 ? (
          <Typography variant="h5" fontWeight={600} sx={{ color: "#FFE941" }}>
            {formatNumber(reportSummary.sales_target)}{" "}
            {!isHeadOfficeAccounting && (
              <EditIcon
                sx={{ fontSize: 16, cursor: "pointer", color: "#fff" }}
                onClick={() => {
                  setOpenInputSaleTarget(true);
                  setSaleTarget(reportSummary.sales_target);
                }}
              />
            )}
          </Typography>
        ) : !openInputSaleTarget ? (
          <Box>
            <Button
              variant="outlined"
              sx={{ color: "#fff", border: "1px solid" }}
              onClick={() => setOpenInputSaleTarget(true)}
            >
              กำหนดเป้ายอดขาย
            </Button>
          </Box>
        ) : (
          <Box>
            <TextField
              size="small"
              type="number"
              variant="standard"
              value={saleTarget}
              onChange={(e) =>
                setSaleTarget(e.target.value ? Number(e.target.value) : null)
              }
              onKeyDown={onUpdateSaleTarget}
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
              sx={{
                bgcolor: "#fff",
                width: "50%",
                "& input": {
                  padding: "8px 0",
                  textAlign: "center",
                },
              }}
            />
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Box
            component="img"
            alt="Gold Bread Target"
            src="/icons/target.png"
            sx={{ height: "12px", width: "12px" }}
          />
          <Typography variant="caption">
            ยอดขาย {formatNumber(reportSummary.actual_sales)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Chip
            label={`${
              reportSummary.actual_sales > reportSummary.sales_target ? "+" : ""
            }${reportSummary.compare_sales_percent}%`}
            variant="filled"
            sx={{
              height: 28,
              fontWeight: 600,
              color: "#fff",
              bgcolor: (theme) =>
                reportSummary.actual_sales > reportSummary.sales_target
                  ? theme.palette.success.main
                  : theme.palette.error.main,
            }}
          />
          <Typography variant="caption">จากเป้ายอดขาย</Typography>
        </Box>
      </CardContent>
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
          ml: 1,
        }}
      >
        <Box
          component="img"
          alt="Gold Bread Target"
          src="/icons/target.png"
          sx={{ height: "32px", width: "32px" }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "#fff",
          }}
        >
          <Typography variant="h5" fontWeight={600} lineHeight={0.8}>
            {numberWithCommas(reportSummary.customer_count)}
          </Typography>
          <Typography variant="h5" fontWeight={600}>
            ร้าน
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}
